/**
 * Super Rotation System wall kicks.
 *
 * The published SRS tables use a y-UP convention (a kick of +1 moves the piece
 * one row up the screen). The rest of this codebase uses y-DOWN. The raw tables
 * below are kept verbatim in the published convention so they can be checked
 * against any reference; `getKicks()` returns them converted to board space
 * (y-down), which is what callers should use.
 */

import type { PieceType, Rotation, Vec } from './piece.ts';

/** Key for a rotation transition, e.g. `0>1`. */
export type KickKey = `${Rotation}>${Rotation}`;

export type KickTable = Record<KickKey, readonly Vec[]>;

/**
 * 180s are not part of published SRS, so the game picks its own offsets.
 *
 * Every piece's 180 shifts its footprint by a row or a column — a T's stem
 * crosses to the other side of the bar, a vertical I moves one column — so a
 * kickless 180 fails against the floor, the stack or a wall, which is very
 * nearly always where a player reaches for it. These offsets try the naive spot
 * first, then lift one row (the floor case), then step aside (the wall case),
 * then drop one (the ceiling case). y is UP-positive, like the tables below.
 */
const KICKS_180: readonly Vec[] = [
  { x: 0, y: 0 },
  { x: 0, y: 1 },
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: -1 },
  { x: 1, y: 1 },
  { x: -1, y: 1 },
];

/** Published table for J, L, S, T, Z. y is UP-positive here. */
export const KICKS_JLSTZ: KickTable = {
  '0>1': [{ x: 0, y: 0 }, { x: -1, y: 0 }, { x: -1, y: 1 }, { x: 0, y: -2 }, { x: -1, y: -2 }],
  '1>0': [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: -1 }, { x: 0, y: 2 }, { x: 1, y: 2 }],
  '1>2': [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: -1 }, { x: 0, y: 2 }, { x: 1, y: 2 }],
  '2>1': [{ x: 0, y: 0 }, { x: -1, y: 0 }, { x: -1, y: 1 }, { x: 0, y: -2 }, { x: -1, y: -2 }],
  '2>3': [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: -2 }, { x: 1, y: -2 }],
  '3>2': [{ x: 0, y: 0 }, { x: -1, y: 0 }, { x: -1, y: -1 }, { x: 0, y: 2 }, { x: -1, y: 2 }],
  '3>0': [{ x: 0, y: 0 }, { x: -1, y: 0 }, { x: -1, y: -1 }, { x: 0, y: 2 }, { x: -1, y: 2 }],
  '0>3': [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: -2 }, { x: 1, y: -2 }],
  '0>2': KICKS_180,
  '2>0': KICKS_180,
  '1>3': KICKS_180,
  '3>1': KICKS_180,
  '0>0': [{ x: 0, y: 0 }],
  '1>1': [{ x: 0, y: 0 }],
  '2>2': [{ x: 0, y: 0 }],
  '3>3': [{ x: 0, y: 0 }],
};

/** Published table for I. y is UP-positive here. */
export const KICKS_I: KickTable = {
  '0>1': [{ x: 0, y: 0 }, { x: -2, y: 0 }, { x: 1, y: 0 }, { x: -2, y: -1 }, { x: 1, y: 2 }],
  '1>0': [{ x: 0, y: 0 }, { x: 2, y: 0 }, { x: -1, y: 0 }, { x: 2, y: 1 }, { x: -1, y: -2 }],
  '1>2': [{ x: 0, y: 0 }, { x: -1, y: 0 }, { x: 2, y: 0 }, { x: -1, y: 2 }, { x: 2, y: -1 }],
  '2>1': [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: -2, y: 0 }, { x: 1, y: -2 }, { x: -2, y: 1 }],
  '2>3': [{ x: 0, y: 0 }, { x: 2, y: 0 }, { x: -1, y: 0 }, { x: 2, y: 1 }, { x: -1, y: -2 }],
  '3>2': [{ x: 0, y: 0 }, { x: -2, y: 0 }, { x: 1, y: 0 }, { x: -2, y: -1 }, { x: 1, y: 2 }],
  '3>0': [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: -2, y: 0 }, { x: 1, y: -2 }, { x: -2, y: 1 }],
  '0>3': [{ x: 0, y: 0 }, { x: -1, y: 0 }, { x: 2, y: 0 }, { x: -1, y: 2 }, { x: 2, y: -1 }],
  '0>2': KICKS_180,
  '2>0': KICKS_180,
  '1>3': KICKS_180,
  '3>1': KICKS_180,
  '0>0': [{ x: 0, y: 0 }],
  '1>1': [{ x: 0, y: 0 }],
  '2>2': [{ x: 0, y: 0 }],
  '3>3': [{ x: 0, y: 0 }],
};

/** O never kicks: its bounding box does not move when rotated. */
export const KICKS_O: readonly Vec[] = [{ x: 0, y: 0 }];

export function kickKey(from: Rotation, to: Rotation): KickKey {
  return `${from}>${to}`;
}

/** Which published table applies to a piece type. */
export function tableFor(type: PieceType): KickTable | null {
  if (type === 'O') return null;
  return type === 'I' ? KICKS_I : KICKS_JLSTZ;
}

/**
 * Kick offsets to try, in order, for a rotation transition — in BOARD space
 * (x right, y down). Index 0 is always the no-offset attempt.
 */
export function getKicks(type: PieceType, from: Rotation, to: Rotation): readonly Vec[] {
  if (type === 'O') return KICKS_O;
  const table = type === 'I' ? KICKS_I : KICKS_JLSTZ;
  const raw = table[kickKey(from, to)];
  // `0 - y` rather than `-y` so a zero offset stays +0, not -0.
  return raw.map((offset) => ({ x: offset.x, y: 0 - offset.y }));
}

/** True when the transition is a 180. */
export function is180(from: Rotation, to: Rotation): boolean {
  return (from + 2) % 4 === to;
}
